/* ProductVariantPageClient - Client component for detailed variant page with image gallery and specifications
 * Updated: H1 now uses "Koop jouw {plantNaam} bij de haagplanten specialist in Twente" format.
 *           Omschrijving section now renders product description as markdown (sanitized) without a card wrapper.
 *           Breadcrumb includes subcategory step and uses base_slug for the product link.
 * Updated: Added DataFast product_variant_viewed goal when variant data loads. */

const PUBLIC_VARIANT_COLUMNS =
  'id, product_id, size, type, slug, sku, active, price_tier_1, price_tier_2, price_tier_3, price_tier_4, planten_per_meter, minimum_order_quantity, stock_quantity, supplier, transport_method, plants_per_box, boxes_per_pallet_position, available_from, created_at, updated_at';


import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { marked } from 'marked';

marked.setOptions({ breaks: true, gfm: true, pedantic: false });
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb, { Crumb } from '@/components/PageBreadcrumb';
import ProductVariantDetails from '@/components/product/ProductVariantDetails';
import ProductSpecifications from '@/components/product/ProductSpecifications';
import RelatedVariants from '@/components/product/RelatedVariants';
import ProductFAQ from '@/components/product/ProductFAQ';
import ProductReviews from '@/components/product/ProductReviews';
import FrequentlyBoughtTogether from '@/components/product/FrequentlyBoughtTogether';
import ServiceBenefits from '@/components/product/ServiceBenefits';
import OfferteCTA from '@/components/product/OfferteCTA';
import { ArrowRight } from 'lucide-react';
import NotFoundPage from '@/pages/NotFoundPage';
import { Loader as Loader2 } from 'lucide-react';
import { trackGoal } from '@/lib/datafast';

interface Product {
  id: string;
  name: string;
  scientific_name: string;
  description: string;
  standplaats: string[];
  groeisnelheid: string;
  snoeien: string;
  groei_per_jaar: string;
  wintergroen: boolean;
  winterhard: boolean;
  maximale_hoogte: string;
  geslacht: string;
  category_name: string;
  category_slug: string;
  base_slug: string;
  subcategory_name: string | null;
  subcategory_slug: string | null;
  subcategory_id: string | null;
}

interface ProductVariant {
  id: string;
  size: string;
  type: string;
  price: string;
  price_tier_1?: number;
  price_tier_2?: number | null;
  price_tier_3?: number | null;
  price_tier_4?: number | null;
  stock_quantity: number;
  sku: string;
  images: string[];
  variantImages: string[];
  genericImages: string[];
  planten_per_meter?: number | null;
  minimum_order_quantity?: number;
  supplier?: string;
  available_from?: string | null;
}

interface ProductFAQItem {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

export default function ProductVariantPageClient() {
  const params = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [variant, setVariant] = useState<ProductVariant | null>(null);
  const [relatedVariants, setRelatedVariants] = useState<ProductVariant[]>([]);
  const [faqs, setFaqs] = useState<ProductFAQItem[]>([]);
  const [loading, setLoading] = useState(true);

  const descriptionHtml = useMemo(() => {
    if (!product?.description) return '';
    return sanitizeHtml(marked.parse(product.description) as string);
  }, [product?.description]);

  useEffect(() => {
    const fetchVariantData = async () => {
      if (!params?.product || !params?.category) return;

      try {
        setLoading(true);

        const variantSlug = `${params.category}/${params.product}`;

        const { data: variantData, error: variantError } = await supabase
          .from('product_variants')
          .select(PUBLIC_VARIANT_COLUMNS)
          .eq('slug', variantSlug)
          .eq('active', true)
          .maybeSingle();

        if (variantError) throw variantError;
        if (!variantData) {
          setLoading(false);
          return;
        }

        const { data: productInfo, error: productError } = await supabase
          .from('products')
          .select(`
            id,
            name,
            scientific_name,
            description,
            standplaats,
            groeisnelheid,
            snoeien,
            groei_per_jaar,
            wintergroen,
            winterhard,
            maximale_hoogte,
            geslacht,
            base_slug,
            btw_percentage,
            subcategory_id,
            product_categories(name, slug)
          `)
          .eq('id', variantData.product_id)
          .eq('active', true)
          .maybeSingle();

        if (productError) throw productError;
        if (!productInfo) return;

        let subcategoryName: string | null = null;
        let subcategorySlug: string | null = null;
        if (productInfo.subcategory_id) {
          const { data: subData } = await supabase
            .from('product_subcategories')
            .select('name, slug')
            .eq('id', productInfo.subcategory_id)
            .maybeSingle();
          if (subData) {
            subcategoryName = subData.name;
            subcategorySlug = subData.slug;
          }
        }

        setProduct({
          id: productInfo.id,
          name: productInfo.name,
          scientific_name: productInfo.scientific_name,
          description: productInfo.description,
          standplaats: productInfo.standplaats || [],
          groeisnelheid: productInfo.groeisnelheid,
          snoeien: productInfo.snoeien,
          groei_per_jaar: productInfo.groei_per_jaar,
          wintergroen: productInfo.wintergroen,
          winterhard: productInfo.winterhard,
          maximale_hoogte: productInfo.maximale_hoogte,
          geslacht: productInfo.geslacht,
          category_name: (productInfo.product_categories as any)?.name || '',
          category_slug: (productInfo.product_categories as any)?.slug || '',
          base_slug: productInfo.base_slug,
          subcategory_name: subcategoryName,
          subcategory_slug: subcategorySlug,
          subcategory_id: productInfo.subcategory_id || null
        });

        const { data: variantImages } = await supabase
          .from('product_images')
          .select('storage_path, uses_new_bucket')
          .eq('product_id', productInfo.id)
          .eq('variant_id', variantData.id)
          .order('display_order');

        const { data: genericImages } = await supabase
          .from('product_images')
          .select('storage_path, uses_new_bucket')
          .eq('product_id', productInfo.id)
          .is('variant_id', null)
          .order('display_order');

        const variantImageUrls = variantImages?.map((img: { storage_path: string; uses_new_bucket: boolean }) =>
          getProductImageUrl(img.storage_path, img.uses_new_bucket, 'large')
        ) || [];

        const genericImageUrls = genericImages?.map((img: { storage_path: string; uses_new_bucket: boolean }) =>
          getProductImageUrl(img.storage_path, img.uses_new_bucket, 'large')
        ) || [];

        const allImageUrls = [...variantImageUrls, ...genericImageUrls];

        const priceExclBTW = parseFloat(variantData.price_tier_1) || 0;
        const btwPercentage = productInfo.btw_percentage || 9;
        const priceInclBTW = calculatePriceInclBTW(priceExclBTW, btwPercentage);

        const priceTier2Incl = variantData.price_tier_2 ? calculatePriceInclBTW(parseFloat(variantData.price_tier_2), btwPercentage) : null;
        const priceTier3Incl = variantData.price_tier_3 ? calculatePriceInclBTW(parseFloat(variantData.price_tier_3), btwPercentage) : null;
        const priceTier4Incl = variantData.price_tier_4 ? calculatePriceInclBTW(parseFloat(variantData.price_tier_4), btwPercentage) : null;

        setVariant({
          id: variantData.id,
          size: variantData.size,
          type: variantData.type,
          price: priceInclBTW.toString(),
          price_tier_1: priceInclBTW,
          price_tier_2: priceTier2Incl,
          price_tier_3: priceTier3Incl,
          price_tier_4: priceTier4Incl,
          stock_quantity: variantData.stock_quantity,
          sku: variantData.sku,
          images: allImageUrls,
          variantImages: variantImageUrls,
          genericImages: genericImageUrls,
          planten_per_meter: variantData.planten_per_meter,
          minimum_order_quantity: variantData.minimum_order_quantity,
          supplier: variantData.supplier || 'Kwekerij',
          available_from: variantData.available_from || null
        });

        trackGoal('product_variant_viewed', { name: productInfo.name, size: variantData.size, type: variantData.type });

        const { data: allVariants, error: variantsError } = await supabase
          .from('product_variants')
          .select(PUBLIC_VARIANT_COLUMNS)
          .eq('product_id', productInfo.id)
          .eq('active', true);

        if (variantsError) throw variantsError;

        if (allVariants) {
          const variantsWithImages = await Promise.all(
            allVariants.map(async (v: any) => {
              const { data: vImages } = await supabase
                .from('product_images')
                .select('storage_path, uses_new_bucket')
                .eq('product_id', productInfo.id)
                .eq('variant_id', v.id)
                .order('display_order')
                .limit(1);

              const { data: gImages } = await supabase
                .from('product_images')
                .select('storage_path, uses_new_bucket')
                .eq('product_id', productInfo.id)
                .is('variant_id', null)
                .order('display_order')
                .limit(1);

              const vImageUrl = vImages?.[0] ? getProductImageUrl(vImages[0].storage_path, vImages[0].uses_new_bucket, 'small') : null;
              const gImageUrl = gImages?.[0] ? getProductImageUrl(gImages[0].storage_path, gImages[0].uses_new_bucket, 'small') : null;

              const relPriceExclBTW = parseFloat(v.price_tier_1) || 0;
              const relPriceInclBTW = calculatePriceInclBTW(relPriceExclBTW, btwPercentage);

              return {
                id: v.id,
                size: v.size,
                type: v.type,
                price: relPriceInclBTW.toString(),
                stock_quantity: v.stock_quantity,
                sku: v.sku,
                slug: v.slug,
                images: [vImageUrl || gImageUrl].filter(Boolean) as string[],
                variantImages: vImageUrl ? [vImageUrl] : [],
                genericImages: gImageUrl ? [gImageUrl] : [],
                planten_per_meter: v.planten_per_meter,
                minimum_order_quantity: v.minimum_order_quantity
              };
            })
          );

          const sortedVariants = variantsWithImages.sort((a, b) => {
            const aNum = parseInt(a.size.split('-')[0]);
            const bNum = parseInt(b.size.split('-')[0]);
            return aNum - bNum;
          });

          setRelatedVariants(sortedVariants);
        }

        const { data: faqsData, error: faqsError } = await supabase
          .from('product_faqs')
          .select('*')
          .eq('product_id', productInfo.id)
          .eq('active', true)
          .order('display_order');

        if (faqsError) throw faqsError;

        if (faqsData) {
          setFaqs(faqsData);
        }
      } catch (error) {
        console.error('Error fetching variant data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVariantData();
  }, [params?.product, params?.category]);

  if (loading) {
    return (
      <>
        <ContactBar />
        <div className="min-h-screen flex items-center justify-center bg-[#fcfaf6]">
          <Loader2 className="h-10 w-10 animate-spin text-[#1e2b22]" />
        </div>
      </>
    );
  }

  if (!product || !variant) {
    return <NotFoundPage />;
  }

  const hasMultipleVariants = relatedVariants.length > 1;
  const variantBreadcrumbLabel = `${product.name} ${variant.size}`;

  const pageTitle = `${product.name} ${variant.size} ${variant.type} kopen | Buitn Planten`;
  const pageDescription = `${product.name} (${product.scientific_name}) ${variant.size} ${variant.type} kopen bij Buitn Planten. Topkwaliteit haagplanten uit Dedemsvaart. Direct online bestellen.`;
  const pageImage = variant.images?.[0] || 'https://buitnplanten.nl/assets/sections/hero.webp';
  const pageUrl = `https://buitnplanten.nl/${product.category_slug}/${product.base_slug}-${variant.size.toLowerCase().replace(/cm/g, '')}-${variant.type.toLowerCase().replace(/\s+/g, '-')}`;

  const breadcrumbItems: Crumb[] = [
    { label: 'Home', href: '/' },
    { label: product.category_name, href: `/${product.category_slug}` },
    ...(product.subcategory_name && product.subcategory_slug
      ? [{ label: product.subcategory_name, href: `/${product.category_slug}/${product.subcategory_slug}` } as Crumb]
      : []),
    ...(hasMultipleVariants
      ? [{ label: product.name, href: `/${product.base_slug}` } as Crumb]
      : []),
    { label: variantBreadcrumbLabel },
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${product.name}, ${product.scientific_name}, ${variant.size}, ${variant.type}, haagplanten, ${product.category_name}, Buitn Planten, Dedemsvaart, kopen`} />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="product:price:amount" content={variant.price} />
        <meta property="product:price:currency" content="EUR" />
        <meta property="product:availability" content={variant.stock_quantity > 0 ? "in stock" : "out of stock"} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": `${product.name} ${variant.size} ${variant.type}`,
            "description": pageDescription,
            "image": pageImage,
            "brand": { "@type": "Brand", "name": "Buitn Planten" },
            "category": product.category_name,
            "offers": {
              "@type": "Offer",
              "url": pageUrl,
              "priceCurrency": "EUR",
              "price": variant.price,
              "availability": variant.stock_quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              "seller": { "@type": "Organization", "name": "Buitn Planten" }
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buitnplanten.nl/" },
              { "@type": "ListItem", "position": 2, "name": product.category_name, "item": `https://buitnplanten.nl/${product.category_slug}` },
              ...(product.subcategory_name ? [{ "@type": "ListItem", "position": 3, "name": product.subcategory_name, "item": `https://buitnplanten.nl/${product.category_slug}/${product.subcategory_slug}` }] : []),
              ...(hasMultipleVariants ? [{ "@type": "ListItem", "position": product.subcategory_name ? 4 : 3, "name": product.name, "item": `https://buitnplanten.nl/${product.base_slug}` }] : []),
              { "@type": "ListItem", "position": (product.subcategory_name ? 1 : 0) + (hasMultipleVariants ? 1 : 0) + 3, "name": variantBreadcrumbLabel, "item": pageUrl }
            ]
          })}
        </script>

        {faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": { "@type": "Answer", "text": faq.answer.replace(/<[^>]*>/g, '') }
              }))
            })}
          </script>
        )}
      </Helmet>

      <ContactBar />
      <div className="min-h-screen bg-[#fcfaf6]">
        <Header />

        <main className="mx-auto max-w-[1240px] px-5 pt-8 pb-20 lg:px-8">
          <PageBreadcrumb items={breadcrumbItems} />

          <ProductVariantDetails
            product={product}
            variant={variant}
            relatedVariants={relatedVariants.filter(v => v.id !== variant.id)}
            allVariants={relatedVariants.map(v => ({ id: v.id, size: v.size, type: v.type, slug: v.slug, stock_quantity: v.stock_quantity }))}
            productSlug={product.base_slug}
            categorySlug={product.category_slug}
          />

          <ServiceBenefits />

          <div className="sticky top-[176px] z-10 mb-10 hidden overflow-x-auto border-y border-[#e7e1d6] bg-[#fcfaf6]/95 py-3 backdrop-blur lg:block">
            <nav className="flex min-w-max items-center justify-center gap-8 text-sm font-medium text-[#5a6552]" aria-label="Productinformatie">
              <a href="#omschrijving" className="tab-underline transition-colors hover:text-[#1e2b22]">Omschrijving</a>
              <a href="#specificaties" className="tab-underline transition-colors hover:text-[#1e2b22]">Specificaties</a>
              <a href="#reviews" className="tab-underline transition-colors hover:text-[#1e2b22]">Reviews</a>
              <a href="#veelgestelde-vragen" className="tab-underline transition-colors hover:text-[#1e2b22]">Veelgestelde vragen</a>
            </nav>
          </div>

          {product.description && (
            <section id="omschrijving" className="mb-16 scroll-mt-48">
              <div className="mb-6">
                <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Over deze plant</p>
                <h2 className="text-3xl tracking-[-0.035em] text-black sm:text-4xl">Omschrijving</h2>
              </div>
              <div
                className="prose prose-lg max-w-none text-[#1a1a1a] [&_a]:text-[#1e2b22] [&_a]:underline [&_p]:mb-4 [&_p]:leading-7 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-[#1e2b22] [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-[#1e2b22] [&_h3]:mt-5 [&_h3]:mb-2 [&_strong]:text-[#1e2b22] [&_blockquote]:border-l-2 [&_blockquote]:border-[#e7e1d6] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#5a6552]"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            </section>
          )}

          <section id="specificaties" className="scroll-mt-48">
            <ProductSpecifications product={product} variant={variant} />
          </section>

          <section id="reviews" className="mb-16 scroll-mt-48">
            <div className="mb-6">
              <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Wat klanten zeggen</p>
              <h2 className="text-3xl tracking-[-0.035em] text-black sm:text-4xl">Reviews</h2>
              <p className="mt-3 text-base leading-7 text-[#1a1a1a]">Ervaringen van klanten die met Buitn Planten aan de slag gingen.</p>
            </div>
            <ProductReviews />
          </section>

          {faqs.length > 0 && (
            <section id="veelgestelde-vragen" className="mb-16 scroll-mt-48">
              <div className="mb-6">
                <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Vragen</p>
                <h2 className="text-3xl tracking-[-0.035em] text-black sm:text-4xl">Veelgestelde vragen</h2>
                <p className="mt-3 text-base leading-7 text-[#1a1a1a]">Antwoorden op de meest gestelde vragen over dit product.</p>
              </div>
              <ProductFAQ faqs={faqs} />
            </section>
          )}

          <section className="mb-16">
            <div className="mb-8">
              <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Alternatieven</p>
              <h2 className="text-3xl tracking-[-0.035em] text-black md:text-4xl">Op zoek naar alternatieven?</h2>
              <p className="mt-3 text-base leading-7 text-[#1a1a1a]">
                Bekijk onze andere planten uit dezelfde categorie of ons volledige assortiment aan hoogwaardige haagplanten.
              </p>
            </div>
            <FrequentlyBoughtTogether
              currentProductId={product.id}
              subcategoryId={product.subcategory_id}
              categorySlug={product.category_slug}
            />
            <div className="mt-8 text-center">
              <a
                href="/assortiment"
                className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-md bg-[#1e2b22] px-6 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(30,43,34,0.18)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#5a6552] sm:px-7"
              >
                Naar assortiment
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </section>

          <OfferteCTA />
        </main>

        <Footer />
      </div>
    </>
  );
}
